import { useQueryClient } from "@tanstack/react-query";
import { useState, useEffect, useCallback, useRef, useMemo } from "react";

import type {
  ScannerAuthStrategy,
  ScannerCounters,
  ScannerDiscovery,
  ScannerModule,
  ScannerPhase,
  ScannerTreeNode,
  TestConnStatus,
  UrlValidationStatus,
} from "./scanner.types";

import { useSiteSyncSse } from "@/common/hooks/useSiteSyncSse";
import { useToast } from "@/common/hooks/useToast";
import { validateURLAccess } from "@/common/services/scenarios";
import { useUpdateProject } from "@/common/services/settings/project/settings.mutations";
import {
  useSiteSyncStatus,
  useStartSiteSync,
  useResyncSite,
  useCancelSiteSync,
  useSiteSyncPages,
  SiteSyncStartPayload,
  testSiteAuth,
} from "@/common/services/site-sync";

export interface UseScannerControllerArgs {
  /** Workspace version to sync against — required to run a real crawl. */
  versionId?: number;
  /** Project id (optional) — lets the scanner persist a changed URL back. */
  projectId?: number | null;
  /** Project object (optional) — used to prefill the app URL. */
  project?: { websiteurl?: string; versionId?: number } | null;
  /** Explicit initial URL (overrides project.websiteurl). */
  initialUrl?: string;
  /** Whether the hosting dialog is open — gates queries/SSE while closed. */
  active?: boolean;
}

const titleCase = (s: string) =>
  s
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();

// Lightweight LIVE fallback for a page's functional MODULE while a crawl is streaming.
// The backend stamps the authoritative `page.module` at finalize (moduleGrouping.ts, which
// also strips the shared container prefix); until that lands, SSE pages carry no module, so
// we derive a decent label here from the route. Mirrors the backend's two signals: (1) an
// ERP screen code's leading alpha prefix (PA000030 → "PA"), then (2) the first meaningful
// path segment (skipping app-shell/container dirs). Auth stays its own module.
// Presentation-only — the persisted `page.module` overwrites this on completion.
const MODULE_SKIP = new Set([
  "scripts",
  "screens",
  "pages",
  "frames",
  "app",
  "apps",
  "home",
  "default",
  "index",
  "main",
  "site",
  "web",
  "portal",
  "public",
  "static",
  "assets",
  "content",
  "view",
  "views",
  "ui",
  "www",
  "dist",
  "build",
  "src",
  "forms",
  "form",
  "frm",
]);
const stripPageExt = (seg: string) =>
  seg.replace(
    /\.(html?|aspx?|php|jsp|jspx|do|action|cfm|cgi|xhtml|razor)$/i,
    "",
  );
const deriveModuleLabel = (routePath: string, category?: string): string => {
  if ((category || "").toLowerCase() === "auth") return "Authentication";
  const path = (routePath || "").split(/[?#]/)[0];
  const segments = path
    .split("/")
    .map((s) => s.trim())
    .filter(Boolean);
  for (const seg of segments) {
    const m = stripPageExt(seg).match(/^([a-z]{2,4})[-_]?\d{3,}$/i);
    if (m) return m[1].toUpperCase();
  }
  for (const seg of segments) {
    const bare = stripPageExt(seg);
    const low = bare.toLowerCase();
    if (
      !bare ||
      MODULE_SKIP.has(low) ||
      /^\d+$/.test(bare) ||
      /^[0-9a-f]{8,}$/i.test(bare)
    )
      continue;
    return titleCase(bare);
  }
  return titleCase(category || "") || "General";
};
// The module label for a discovered page: authoritative persisted value when present,
// else the live route-derived fallback.
const pageModule = (p: any): string =>
  (p?.module && String(p.module)) ||
  deriveModuleLabel(p?.routePath ?? p?.url ?? "", p?.category);

// True for an Axios client-side timeout / aborted request where NO server response
// was ever received. A real 4xx/5xx carries `err.response`, so it can never be
// mistaken for a timeout. Used to tell "the start request timed out (the crawl is
// almost certainly still spawning server-side)" apart from "the server actively
// rejected the start".
const isStartTimeout = (err: any): boolean =>
  !err?.response &&
  (err?.code === "ECONNABORTED" ||
    err?.code === "ETIMEDOUT" ||
    /timeout/i.test(err?.message || ""));

/**
 * Encapsulates the *entire* site-sync crawl flow (same endpoints + SSE the
 * project AI Sync tab uses) and derives the presentation data for the redesigned
 * scanner dialog. Nothing here is simulated — every counter, page and status
 * comes from the live crawl.
 */
export function useScannerController({
  versionId,
  projectId,
  project,
  initialUrl,
  active = true,
}: UseScannerControllerArgs) {
  const [url, setUrl] = useState(initialUrl || project?.websiteurl || "");
  const [authStrategy, setAuthStrategy] = useState<ScannerAuthStrategy>("none");
  const [loginUrl, setLoginUrl] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [token, setToken] = useState("");
  const [apiKeyHeader, setApiKeyHeader] = useState("X-API-Key");
  const [showSecret, setShowSecret] = useState(false);
  const [testConnStatus, setTestConnStatus] = useState<TestConnStatus>("idle");
  const [testConnMessage, setTestConnMessage] = useState<string | null>(null);
  const [urlValidationStatus, setUrlValidationStatus] =
    useState<UrlValidationStatus>("idle");

  // Local step override. `null` => derive the phase from the backend status.
  const [flowStep, setFlowStep] = useState<ScannerPhase | null>(null);

  const urlValidationTimer = useRef<NodeJS.Timeout | null>(null);
  const userSelectedAuthStrategyRef = useRef(false);

  // --- SSE generation-id state machine (ported verbatim from AiSyncTab) --------
  const [sseGenerationId, setSseGenerationId] = useState<string | null>(null);
  const finishedGenerationIdsRef = useRef<Set<string>>(new Set());
  const sseGenerationIdRef = useRef<string | null>(null);
  sseGenerationIdRef.current = sseGenerationId;
  const genStartedAtRef = useRef(0);

  // Self-heal latch for a slow / timed-out "start" request. POST /site-sync/start now
  // returns in milliseconds (the crawl's DB writes were decoupled on the backend), so
  // the 30s client timeout should no longer fire. But if THIS request still times out
  // (a proxy/network blip, or a first-ever-scan INSERT under extreme DB-pool
  // contention) the mutation errors WITHOUT ever handing us a generationId — even
  // though the crawl is almost certainly spawning server-side. `awaitingCrawlStart`
  // keeps the status poll alive across that timeout so the auto-reconnect effect can
  // latch onto the crawl the instant its "crawling" row lands, instead of falsely
  // telling the user the scan never began.
  const [awaitingCrawlStart, setAwaitingCrawlStart] = useState(false);

  // Start-grace guard. There is a brief window between hitting Start/Re-scan and
  // the backend writing the new "crawling" row. During that window getStatus can
  // still return the PREVIOUS crawl's terminal "synced"/"failed" row. Without this
  // guard that stale row trips the terminal-status backstop below, kills the live
  // stream and bounces the user straight to the Insights view — the "nothing
  // happens, I have to click Re-scan twice" bug. We hold the scanning phase (and
  // suppress stale terminal statuses) until the new crawl is confirmed running or
  // the SSE stream itself terminates.
  const scanInitiatedAtRef = useRef(0);
  const activeCrawlConfirmedRef = useRef(false);
  // Set when the user explicitly asks to re-crawl ("Rescan" / "Try again").
  // Consumed by startScan so the backend bypasses its fresh-cache short-circuit
  // and runs a real crawl instead of silently returning the stale cached pages.
  const forceRescanRef = useRef(false);
  const START_GRACE_MS = 15000;
  // Upper bound on how long we keep polling for a crawl to register after a start
  // timeout before standing back down to the normal status-driven polling cadence.
  const AWAIT_START_MAX_MS = 90000;

  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { mutate: updateProject } = useUpdateProject();

  const {
    data: syncStatus,
    isLoading: isStatusLoading,
    dataUpdatedAt: statusUpdatedAt,
  } = useSiteSyncStatus(
    active ? versionId : undefined,
    url || undefined,
    !!sseGenerationId || awaitingCrawlStart,
  );
  const startSync = useStartSiteSync();
  const resyncMutation = useResyncSite();
  const cancelMutation = useCancelSiteSync();
  const { data: pagesData } = useSiteSyncPages(
    active ? syncStatus?.syncId : undefined,
    syncStatus?.status === "crawling",
  );

  const {
    livePages: ssePages,
    progress: sseProgress,
    statusMessage: sseStatusMessage,
    isComplete: sseComplete,
    hasError: sseHasError,
  } = useSiteSyncSse(active ? sseGenerationId : null);

  // Stamp the generation start so the terminal-status backstop can reject stale
  // rows fetched before this generation existed.
  useEffect(() => {
    if (sseGenerationId) {
      genStartedAtRef.current = Date.now();
    }
  }, [sseGenerationId]);

  // SSE complete → refresh queries.
  useEffect(() => {
    if (sseComplete) {
      if (sseGenerationIdRef.current) {
        finishedGenerationIdsRef.current.add(sseGenerationIdRef.current);
      }
      scanInitiatedAtRef.current = 0;
      setSseGenerationId(null);
      setAwaitingCrawlStart(false);
      setFlowStep(null); // let the derived phase settle on "complete"
      queryClient.invalidateQueries({ queryKey: ["siteSyncStatus"] });
      queryClient.invalidateQueries({ queryKey: ["siteSyncPages"] });
    }
  }, [sseComplete, queryClient]);

  // SSE error → leave the syncing state and surface failure.
  useEffect(() => {
    if (sseHasError) {
      if (sseGenerationIdRef.current) {
        finishedGenerationIdsRef.current.add(sseGenerationIdRef.current);
      }
      scanInitiatedAtRef.current = 0;
      setSseGenerationId(null);
      setAwaitingCrawlStart(false);
      queryClient.invalidateQueries({ queryKey: ["siteSyncStatus"] });
    }
  }, [sseHasError, queryClient]);

  // Prefill URL from project.
  useEffect(() => {
    if (!initialUrl && project?.websiteurl) {
      setUrl((prev) => prev || project.websiteurl || "");
    }
  }, [project?.websiteurl, initialUrl]);

  const handleAuthStrategySelect = useCallback(
    (strategy: ScannerAuthStrategy) => {
      userSelectedAuthStrategyRef.current = true;
      setAuthStrategy(strategy);
      setTestConnStatus("idle");
      setTestConnMessage(null);
    },
    [],
  );

  // Adopt backend-detected auth strategy unless the user picked one manually.
  useEffect(() => {
    if (userSelectedAuthStrategyRef.current) return;
    if (syncStatus?.authStrategy && syncStatus.authStrategy !== "none") {
      setAuthStrategy(syncStatus.authStrategy as ScannerAuthStrategy);
      return;
    }
    if (syncStatus?.authDetected) {
      setAuthStrategy("form");
    }
  }, [syncStatus?.authDetected, syncStatus?.authStrategy]);

  // Prefill saved auth credentials from backend ONCE when initial syncStatus arrives.
  // Using a ref prevents subsequent user edits or cleared fields from being overwritten.
  const hasPrefilledAuthRef = useRef(false);

  useEffect(() => {
    hasPrefilledAuthRef.current = false;
  }, [versionId, projectId]);

  useEffect(() => {
    if (hasPrefilledAuthRef.current) return;
    if (syncStatus?.authLoginUrl || syncStatus?.authUsername) {
      if (syncStatus.authLoginUrl) setLoginUrl(syncStatus.authLoginUrl);
      if (syncStatus.authUsername) setUsername(syncStatus.authUsername);
      hasPrefilledAuthRef.current = true;
    }
  }, [syncStatus?.authLoginUrl, syncStatus?.authUsername]);

  // Confirm the new crawl actually registered (a fresh "crawling" — or an
  // immediate "auth_required" — status landed) so we can release the start-grace
  // guard early instead of waiting the full timeout. "auth_required" is only ever
  // produced by a NEW attempt, so honouring it here never re-surfaces a stale row.
  useEffect(() => {
    const s = syncStatus?.status;
    if (
      scanInitiatedAtRef.current > 0 &&
      (s === "crawling" || s === "auth_required")
    ) {
      activeCrawlConfirmedRef.current = true;
    }
  }, [syncStatus?.status]);

  // Terminal-status backstop: only act on a status row fetched AFTER the current
  // generation started (prevents the "Syncing → Re-sync → Syncing" flicker) — and
  // NEVER on a stale terminal "synced"/"failed" that lands during the start-grace
  // window (the "rescan twice / jumps to Insights" bug).
  useEffect(() => {
    const s = syncStatus?.status;
    const isTerminal =
      s === "synced" || s === "failed" || s === "auth_required";
    const statusIsFresh = statusUpdatedAt > genStartedAtRef.current;
    const inStartGrace =
      scanInitiatedAtRef.current > 0 &&
      !activeCrawlConfirmedRef.current &&
      Date.now() - scanInitiatedAtRef.current < START_GRACE_MS;
    // A stale "synced"/"failed" from the PREVIOUS crawl must not bounce us to the
    // Insights/complete view before the new crawl has even registered.
    if ((s === "synced" || s === "failed") && inStartGrace) return;
    if (isTerminal && sseGenerationId && statusIsFresh) {
      scanInitiatedAtRef.current = 0;
      setSseGenerationId(null);
      queryClient.invalidateQueries({ queryKey: ["siteSyncPages"] });
    }
  }, [syncStatus?.status, sseGenerationId, statusUpdatedAt, queryClient]);

  // Auto-connect to SSE if a crawl is already in progress (resume mid-crawl).
  useEffect(() => {
    if (
      syncStatus?.status === "crawling" &&
      syncStatus?.generationId &&
      !sseGenerationId &&
      !finishedGenerationIdsRef.current.has(syncStatus.generationId)
    ) {
      setSseGenerationId(syncStatus.generationId);
    }
  }, [syncStatus?.status, syncStatus?.generationId, sseGenerationId]);

  // Stand down the awaiting-start latch once we've either latched onto the live
  // stream (generationId acquired by the effect above) or a FRESH terminal status has
  // landed past the start-grace window (a very fast crawl that finished before we
  // latched). The grace guard prevents a stale previous-crawl "synced"/"failed" from
  // clearing the latch before the new crawl has even registered.
  useEffect(() => {
    if (!awaitingCrawlStart) return;
    if (sseGenerationId) {
      setAwaitingCrawlStart(false);
      return;
    }
    const s = syncStatus?.status;
    const isTerminal =
      s === "synced" || s === "failed" || s === "auth_required";
    const statusIsFresh =
      scanInitiatedAtRef.current > 0 &&
      statusUpdatedAt > scanInitiatedAtRef.current;
    const inGrace =
      scanInitiatedAtRef.current > 0 &&
      !activeCrawlConfirmedRef.current &&
      Date.now() - scanInitiatedAtRef.current < START_GRACE_MS;
    if (isTerminal && statusIsFresh && !inGrace) setAwaitingCrawlStart(false);
  }, [
    awaitingCrawlStart,
    sseGenerationId,
    syncStatus?.status,
    statusUpdatedAt,
  ]);

  // Safety cap: never poll-await a crawl start forever. If nothing latches within this
  // window, drop back to the normal (status-driven) polling cadence.
  useEffect(() => {
    if (!awaitingCrawlStart) return;
    const t = setTimeout(
      () => setAwaitingCrawlStart(false),
      AWAIT_START_MAX_MS,
    );
    return () => clearTimeout(t);
  }, [awaitingCrawlStart]);

  // --- URL handling -----------------------------------------------------------
  const runUrlValidation = useCallback((inputUrl: string) => {
    if (urlValidationTimer.current) clearTimeout(urlValidationTimer.current);
    const trimmed = inputUrl?.trim();
    if (!trimmed) {
      setUrlValidationStatus("idle");
      return;
    }
    const urlPattern =
      /^https?:\/\/(localhost|[\w-]+(\.[\w-]+)*\.[a-zA-Z]{2,})(:\d+)?(\/\S*)?$/;
    if (!urlPattern.test(trimmed)) {
      setUrlValidationStatus("not-accessible");
      return;
    }
    setUrlValidationStatus("validating");
    urlValidationTimer.current = setTimeout(async () => {
      try {
        const res = await validateURLAccess(trimmed);
        setUrlValidationStatus(
          res?.data?.length === 0 ? "accessible" : "not-accessible",
        );
      } catch {
        setUrlValidationStatus("not-accessible");
      }
    }, 500);
  }, []);

  const changeUrl = useCallback(
    (next: string) => {
      setUrl(next);
      setTestConnStatus("idle");
      setTestConnMessage(null);
      runUrlValidation(next);
    },
    [runUrlValidation],
  );

  // A password is saved (encrypted) for the currently-targeted app. `syncStatus`
  // is keyed on the URL, so this automatically reflects the host in the field.
  const hasSavedPassword = !!syncStatus?.hasPassword;

  const buildAuthPayload = useCallback(():
    | SiteSyncStartPayload["auth"]
    | undefined => {
    if (authStrategy === "none") return undefined;
    // Blank field + a saved password on file → reuse it without ever sending the
    // secret to the client. A typed value always overrides the saved password.
    const reuseSavedPassword = hasSavedPassword && !password.trim();
    return {
      strategy: authStrategy,
      ...(authStrategy === "form" && {
        loginUrl,
        username,
        ...(reuseSavedPassword ? { useSavedPassword: true } : { password }),
      }),
      ...(authStrategy === "token" && { token }),
      ...(authStrategy === "api_key" && { apiKey: token, apiKeyHeader }),
    };
  }, [
    authStrategy,
    hasSavedPassword,
    loginUrl,
    username,
    password,
    token,
    apiKeyHeader,
  ]);

  const normalizeUrl = (raw: string) => {
    let n = raw.trim();
    if (!n.startsWith("http://") && !n.startsWith("https://"))
      n = `https://${n}`;
    return n.replace(/\/$/, "");
  };

  // --- Actions ----------------------------------------------------------------
  const startScan = useCallback(() => {
    if (!url.trim() || !versionId) return;
    const normalizedUrl = normalizeUrl(url);
    setUrl(normalizedUrl);

    const urlChanged = normalizedUrl !== project?.websiteurl;
    if (urlChanged && projectId) {
      updateProject(
        { id: projectId, payload: { websiteurl: normalizedUrl } },
        {
          onSuccess: () =>
            queryClient.invalidateQueries({
              queryKey: ["projectById", projectId],
            }),
        },
      );
    }

    const payload: SiteSyncStartPayload = {
      workspaceVersionId: versionId,
      appUrl: normalizedUrl,
      auth: buildAuthPayload(),
      force: forceRescanRef.current,
    };
    // Consume the one-shot rescan intent so a later non-rescan start doesn't force.
    forceRescanRef.current = false;

    setFlowStep(null);
    scanInitiatedAtRef.current = Date.now();
    activeCrawlConfirmedRef.current = false;
    setAwaitingCrawlStart(true);
    startSync.mutate(payload, {
      onSuccess: (data) => {
        if (data?.generationId) {
          finishedGenerationIdsRef.current.delete(data.generationId);
          setSseGenerationId(data.generationId);
        }
        queryClient.invalidateQueries({ queryKey: ["siteSyncStatus"] });
      },
      onError: (err: any) => {
        // A client-side START TIMEOUT does NOT mean the crawl failed to begin — the
        // backend acks in ms and the crawl spawns independently, so a lost/slow
        // response almost always still produced a running crawl. Keep the awaiting
        // latch (status keeps polling) so the auto-reconnect effect can latch onto it,
        // and suppress the misleading "could not start" toast. Any real server
        // rejection (which carries a response) falls through to the normal error path.
        if (isStartTimeout(err)) return;
        setAwaitingCrawlStart(false);
        scanInitiatedAtRef.current = 0;
        const serverMsg =
          err?.response?.data?.error ||
          err?.data?.error ||
          err?.message ||
          "Could not start sync. Please check your details and try again.";
        const code = err?.response?.data?.code || err?.data?.code;
        if (code === "PASSWORD_REQUIRED") setPassword("");
        showToast({
          title: "Scan could not start",
          description: serverMsg,
          variant: "error",
        });
      },
    });
  }, [
    url,
    versionId,
    projectId,
    project?.websiteurl,
    buildAuthPayload,
    startSync,
    updateProject,
    queryClient,
    showToast,
  ]);

  const resync = useCallback(() => {
    if (syncStatus?.syncId) {
      setFlowStep(null);
      scanInitiatedAtRef.current = Date.now();
      activeCrawlConfirmedRef.current = false;
      setAwaitingCrawlStart(true);
      resyncMutation.mutate(
        { syncId: syncStatus.syncId, auth: buildAuthPayload() },
        {
          onSuccess: (data) => {
            if (data?.generationId) {
              finishedGenerationIdsRef.current.delete(data.generationId);
              setSseGenerationId(data.generationId);
            }
            queryClient.invalidateQueries({ queryKey: ["siteSyncStatus"] });
            queryClient.invalidateQueries({ queryKey: ["siteSyncPages"] });
          },
          onError: (err: any) => {
            // See startScan: a start timeout keeps the awaiting latch so we reconnect
            // to the crawl that almost certainly started; only real errors stand down.
            if (isStartTimeout(err)) return;
            setAwaitingCrawlStart(false);
            scanInitiatedAtRef.current = 0;
          },
        },
      );
    } else {
      startScan();
    }
  }, [
    syncStatus?.syncId,
    buildAuthPayload,
    resyncMutation,
    queryClient,
    startScan,
  ]);

  /** Called from the auth step: run the crawl with the supplied credentials. */
  const submitAuthAndScan = useCallback(() => {
    if (syncStatus?.status === "auth_required" && syncStatus?.syncId) {
      resync();
    } else {
      startScan();
    }
  }, [syncStatus?.status, syncStatus?.syncId, resync, startScan]);

  const cancel = useCallback(() => {
    if (!syncStatus?.syncId) return;
    cancelMutation.mutate(syncStatus.syncId, {
      onSuccess: () => {
        setSseGenerationId(null);
        setAwaitingCrawlStart(false);
        setFlowStep(null);
        queryClient.invalidateQueries({ queryKey: ["siteSyncStatus"] });
        queryClient.invalidateQueries({ queryKey: ["siteSyncPages"] });
      },
    });
  }, [syncStatus?.syncId, cancelMutation, queryClient]);

  // Detach the live view WITHOUT stopping the crawl. The crawl runs server-side, so
  // closing the dialog simply tears down this SSE/polling view; the scan keeps running
  // and reopening the scanner resumes the live view mid-crawl. Deliberately the opposite
  // of `cancel` — it never calls the cancel mutation.
  const runInBackground = useCallback(() => {
    showToast({
      title: "Scan running in the background",
      description:
        "We'll keep crawling. Reopen the scanner anytime to check progress.",
      variant: "info",
    });
  }, [showToast]);

  const testConnection = useCallback(async () => {
    if (!url.trim()) return;
    setTestConnStatus("testing");
    setTestConnMessage(null);
    try {
      const result = await testSiteAuth(normalizeUrl(url), buildAuthPayload());
      if (result.data?.success) {
        setTestConnStatus("success");
        setTestConnMessage(
          result.data.message ??
            "Connection successful — credentials verified.",
        );
      } else {
        setTestConnStatus("failed");
        setTestConnMessage(
          result.data?.message ??
            "Authentication failed. Please check your credentials.",
        );
      }
    } catch (err: any) {
      setTestConnStatus("failed");
      setTestConnMessage(
        err?.response?.data?.message ??
          "Could not connect. Please verify the URL and credentials.",
      );
    }
  }, [url, buildAuthPayload]);

  // --- Derived state ----------------------------------------------------------
  const isSyncing =
    syncStatus?.status === "crawling" ||
    startSync.isPending ||
    resyncMutation.isPending ||
    !!sseGenerationId ||
    awaitingCrawlStart;
  const isSynced = syncStatus?.status === "synced";
  const isFailed = syncStatus?.status === "failed";
  const isAuthRequired = syncStatus?.status === "auth_required";
  // A "synced" row is PARTIAL when it only exists because an interrupted crawl
  // still captured usable pages (backend heals status "failed"+pages → "synced"
  // and flags `partial`). Surfaced so the complete view shows the SAME "last
  // crawl was interrupted" hint the AI Sync tab / generator show — otherwise the
  // Scanner looked clean while the generator warned "interrupted", which read as
  // the two views being out of sync.
  const isPartial = isSynced && !!syncStatus?.partial;

  // True while we own a *live* crawl stream — we just kicked one off (mutation
  // pending) or the SSE channel is open. While this holds, the streamed SSE pages
  // are the ONLY source of truth for the CURRENT crawl. We must never fall back to
  // the previous crawl's persisted DB rows (`pagesData`) or its summary
  // (`syncStatus.crawlStats`), otherwise a fresh re-scan opens showing the OLD
  // totals (e.g. "23 discovered / 15 forms") instead of climbing from zero.
  const hasLiveStream =
    !!sseGenerationId ||
    startSync.isPending ||
    resyncMutation.isPending ||
    awaitingCrawlStart;

  const discoveredPages = useMemo(() => {
    // Live crawl → trust only the streamed pages. `useSiteSyncSse` resets these to
    // [] for each new generation, so the count honestly climbs 0 → N.
    if (hasLiveStream) return ssePages;
    // Idle / observing: show the persisted pages (last completed sync, or a crawl
    // we're watching via DB polling without an SSE channel of our own).
    return pagesData?.pages || [];
  }, [hasLiveStream, pagesData?.pages, ssePages]);

  // The backend now persists an honest, GENERATION-SCOPED live count on the
  // "crawling" row (`crawl_pages_found` / `crawl_progress`, reset to 0 on every
  // fresh crawl's flip, updated by the ~10s progress flush). getStatus exposes
  // them ONLY while status === "crawling", so they always describe the CURRENT
  // crawl — never a previous total. Use them as a FLOOR under the streamed values:
  // if the SSE page feed stalls, or the dialog is reopened mid-crawl (which resets
  // `ssePages` to [] and only replays the buffered tail), the headline count/bar
  // still climb with the real DB progress instead of freezing behind it (the
  // "stuck on 4 while 58 found" bug).
  const isCrawlingNow = syncStatus?.status === "crawling";
  const liveDbPagesFound = isCrawlingNow ? syncStatus?.crawlPagesFound || 0 : 0;
  const liveDbProgress = isCrawlingNow ? syncStatus?.crawlProgress || 0 : 0;

  const displayProgress = hasLiveStream
    ? Math.max(sseProgress, liveDbProgress)
    : syncStatus?.crawlProgress || 0;
  const displayPagesFound = hasLiveStream
    ? Math.max(discoveredPages.length, liveDbPagesFound)
    : discoveredPages.length || syncStatus?.crawlPagesFound || 0;
  const displayEstimatedTotal = syncStatus?.crawlEstimatedTotal || 0;

  const displayHost = useMemo(() => {
    try {
      return new URL(url.startsWith("http") ? url : `https://${url}`).host;
    } catch {
      return url;
    }
  }, [url]);

  // Discovery timeline (newest first).
  const discoveries = useMemo<ScannerDiscovery[]>(() => {
    const seen = new Set<string>();
    const out: ScannerDiscovery[] = [];
    for (let i = discoveredPages.length - 1; i >= 0; i--) {
      const p: any = discoveredPages[i];
      const routePath = p.routePath ?? p.url ?? "/";
      if (seen.has(routePath)) continue;
      seen.add(routePath);
      out.push({
        id: routePath,
        routePath,
        name: p.name || routePath,
        category: p.category || "other",
        module: pageModule(p),
        isAuthProtected: p.isAuthProtected,
      });
    }
    return out;
  }, [discoveredPages]);

  // Modules = functional clusters (backend `page.module`, live route-derived fallback).
  const modules = useMemo<ScannerModule[]>(() => {
    const counts = new Map<string, number>();
    discoveredPages.forEach((p: any) => {
      const key = pageModule(p) || "General";
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    const max = Math.max(1, ...Array.from(counts.values()));
    return Array.from(counts.entries())
      .map(([name, pages]) => ({
        name,
        pages,
        ratio: Math.round((pages / max) * 100),
      }))
      .sort((a, b) => b.pages - a.pages);
  }, [discoveredPages]);

  // Application map tree = module → pages.
  const tree = useMemo<ScannerTreeNode[]>(() => {
    const groups = new Map<
      string,
      { routePath: string; isAuthProtected?: boolean }[]
    >();
    discoveredPages.forEach((p: any) => {
      const key = pageModule(p) || "General";
      const routePath = p.routePath ?? p.url ?? "/";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push({ routePath, isAuthProtected: p.isAuthProtected });
    });
    return Array.from(groups.entries())
      .map(([name, pages]) => ({ name, pages }))
      .sort((a, b) => b.pages.length - a.pages.length);
  }, [discoveredPages]);

  const counters = useMemo<ScannerCounters>(() => {
    // Live crawl → derive every card from the streamed pages so they climb from
    // zero. `syncStatus.crawlStats` still summarises the PREVIOUS crawl until this
    // run finishes, so it must not seed the live counters. (Forms aren't streamed
    // per-page over SSE, so that card fills in on completion.)
    if (hasLiveStream) {
      return {
        routes: Math.max(discoveredPages.length, liveDbPagesFound),
        forms: 0,
        modules: modules.length,
        protectedRoutes: discoveredPages.filter((p: any) => p.isAuthProtected)
          .length,
        publicRoutes: discoveredPages.filter((p: any) => !p.isAuthProtected)
          .length,
      };
    }
    const stats: any = syncStatus?.crawlStats || {};
    return {
      routes: stats.totalRoutes || displayPagesFound || 0,
      forms: stats.formsFound || 0,
      modules: modules.length,
      protectedRoutes:
        stats.authProtectedRoutes ||
        discoveredPages.filter((p: any) => p.isAuthProtected).length ||
        0,
      publicRoutes:
        stats.publicRoutes ||
        discoveredPages.filter((p: any) => !p.isAuthProtected).length ||
        0,
    };
  }, [
    hasLiveStream,
    syncStatus?.crawlStats,
    displayPagesFound,
    liveDbPagesFound,
    modules.length,
    discoveredPages,
  ]);

  // Effective phase — the live crawl always wins over any local step. The
  // start-grace window also forces "scanning" so a stale terminal status can't
  // flash the Insights view between clicking Start and the crawl registering.
  const derivedPhase: ScannerPhase = isSyncing
    ? "scanning"
    : isAuthRequired
      ? "auth"
      : isSynced
        ? "complete"
        : isFailed
          ? "complete" // a failed sync renders CompletePhase's FailedState (clear reason + Try again)
          : "connect";
  const inStartGrace =
    scanInitiatedAtRef.current > 0 &&
    !activeCrawlConfirmedRef.current &&
    Date.now() - scanInitiatedAtRef.current < START_GRACE_MS;
  const phase: ScannerPhase =
    isSyncing || inStartGrace ? "scanning" : (flowStep ?? derivedPhase);

  const goToAuth = useCallback(() => setFlowStep("auth"), []);
  const goToConnect = useCallback(() => {
    // "Rescan" / "Try again" — mark intent so the next start forces a real crawl.
    forceRescanRef.current = true;
    setFlowStep("connect");
  }, []);
  const backToConnectFromAuth = useCallback(() => setFlowStep("connect"), []);

  return {
    // identity
    versionId,
    hasVersion: !!versionId,
    // url + validation
    url,
    changeUrl,
    displayHost,
    urlValidationStatus,
    // auth
    authStrategy,
    selectAuthStrategy: handleAuthStrategySelect,
    loginUrl,
    setLoginUrl,
    username,
    setUsername,
    password,
    setPassword,
    hasSavedPassword,
    token,
    setToken,
    apiKeyHeader,
    setApiKeyHeader,
    showSecret,
    setShowSecret,
    testConnStatus,
    testConnMessage,
    testConnection,
    // status
    syncStatus,
    isStatusLoading,
    isSyncing,
    isSynced,
    isPartial,
    isFailed,
    isAuthRequired,
    statusMessage: sseStatusMessage,
    // progress + data
    displayProgress,
    displayPagesFound,
    displayEstimatedTotal,
    discoveries,
    modules,
    tree,
    counters,
    pageCount: syncStatus?.pageCount ?? displayPagesFound,
    lastSyncedAt: syncStatus?.lastSyncedAt,
    errorMessage: syncStatus?.errorMessage,
    // phase + navigation
    phase,
    goToAuth,
    goToConnect,
    backToConnectFromAuth,
    // actions
    startScan,
    resync,
    submitAuthAndScan,
    cancel,
    runInBackground,
    isStarting: startSync.isPending || resyncMutation.isPending,
  };
}

export type ScannerController = ReturnType<typeof useScannerController>;
